import { create } from 'zustand';

// Ephemeral tavern selection — which card the player has focused by tapping,
// and whether Move mode is armed for a board unit.
//
// This is UI state, not game state. It deliberately lives outside
// stores/match.ts: match state is per-match *game* data that gets serialized
// (parked matches) and exercised by the game test suite, and a transient
// focus ring belongs in neither. It's a store rather than component state
// because three separate render roots need it — the tavern layout, the
// HandRail portal, and the ActionBar portal.
//
// `instanceId` guards against index staleness: hand/board arrays shift when
// cards are bought, played, or sold, so consumers must verify the id at the
// stored index still matches before acting (and clear if not).

export type SelectionZone = 'shop' | 'hand' | 'board';

export interface TavernSelection {
  zone: SelectionZone;
  index: number;
  /** instanceId (or card id for shop offers without one) at selection time. */
  instanceId: string | undefined;
}

interface UiSelectionState {
  selection: TavernSelection | null;
  /** Board unit selected AND Move armed: the next slot tap reorders. */
  moveArmed: boolean;
  /**
   * True for the whole life of a drag (dnd-kit start → end/cancel).
   *
   * CardTile reads this to hard-disable its hover preview. Gating on the
   * mouse-button state alone was not enough in practice: a drag re-parents
   * and re-mounts cards under a moving cursor, and any enter event that
   * arrives without the button bit set pops a 280px preview directly over
   * the card being dragged. A single explicit flag is unambiguous.
   */
  dragging: boolean;
  select: (sel: TavernSelection) => void;
  armMove: () => void;
  clear: () => void;
  setDragging: (dragging: boolean) => void;
}

export const useUiSelectionStore = create<UiSelectionState>((set, get) => ({
  selection: null,
  moveArmed: false,
  dragging: false,
  select: (sel) => {
    const prev = get().selection;
    // Tapping the selected card again toggles the selection off.
    if (
      prev &&
      prev.zone === sel.zone &&
      prev.index === sel.index &&
      prev.instanceId === sel.instanceId
    ) {
      set({ selection: null, moveArmed: false });
      return;
    }
    set({ selection: sel, moveArmed: false });
  },
  armMove: () => {
    if (get().selection?.zone !== 'board') return;
    set({ moveArmed: true });
  },
  clear: () => {
    // Avoid a no-op state write (and the render it triggers) when idle —
    // clear() is called from broad places like drag-start and phase changes.
    if (get().selection === null && !get().moveArmed) return;
    set({ selection: null, moveArmed: false });
  },
  setDragging: (dragging) => {
    if (get().dragging === dragging) return;
    set({ dragging });
  },
}));
